// 导航按运营工作组织：监控、统计、资源、路由、策略、财务与系统（docs/04 第 14 节管理后台导航组织）。
// 名称优先准确表达用途，页面标题与菜单名称一致。
import { roleAtLeast, type Role } from "@/app/auth";
import type { NavIconName } from "@/app/navIcons";

/** minRole 是能看到入口的最低角色：admin 是系统管理员及以上，super_admin 只有超级管理员（D181）；不设则人人可见。
 * 后台账号、审计日志与发送测试的接口服务端同样按角色拒绝（docs/04 第 9 节）；组件总览不调接口，只是隐藏入口。
 * devOnly 的页面只在带环境标记的开发、预览环境出现，正式环境（env_label 为空）连入口带路由一起隐藏（D179）。
 * icon 是 Lucide 图标名，侧栏收成图标条时只剩它（#145）。 */
export interface NavEntry { path: string; label: string; icon: NavIconName; minRole?: Exclude<Role, "viewer">; devOnly?: boolean }
export interface NavGroupDef { title: string; items: NavEntry[] }

export const NAV: NavGroupDef[] = [
  {
    title: "监控",
    items: [
      { path: "/overview", label: "运营概览", icon: "layout-dashboard" },
      { path: "/traffic", label: "流量监控", icon: "trending-up" },
      { path: "/messages", label: "消息记录", icon: "messages-square" },
      { path: "/channel-health", label: "通道健康", icon: "activity" },
    ],
  },
  {
    title: "统计",
    items: [
      { path: "/customer-report", label: "营收数据", icon: "chart-column" },
      { path: "/supplier-report", label: "成本报表", icon: "chart-line" },
    ],
  },
  {
    title: "资源",
    items: [
      { path: "/customers", label: "客户管理", icon: "users" },
      { path: "/accounts", label: "客户账号", icon: "key-round" },
      { path: "/suppliers", label: "供应商", icon: "building-2" },
      { path: "/channels", label: "通道配置", icon: "cable" },
    ],
  },
  {
    title: "路由",
    items: [
      { path: "/account-routing", label: "账号路由", icon: "route" },
      { path: "/routing-rules", label: "路由规则", icon: "signpost" },
      { path: "/routing-templates", label: "路由模板", icon: "list-ordered" },
      { path: "/channel-groups", label: "通道组合", icon: "layers" },
      { path: "/numbering", label: "号码前缀", icon: "hash" },
    ],
  },
  {
    title: "策略",
    items: [
      { path: "/dlr-policies", label: "回执策略", icon: "mail-check" },
      { path: "/profit-guards", label: "利润保护", icon: "shield-check" },
      { path: "/alert-thresholds", label: "告警阈值", icon: "bell-ring" },
      { path: "/sender-policies", label: "SID 替换", icon: "replace" },
    ],
  },
  {
    title: "财务",
    items: [
      { path: "/balances", label: "账号余额", icon: "wallet" },
      { path: "/prices", label: "价格配置", icon: "badge-dollar-sign" },
      { path: "/currency-rates", label: "汇率管理", icon: "arrow-left-right" },
      { path: "/price-sync-tasks", label: "价格同步", icon: "history" },
    ],
  },
  {
    title: "系统",
    items: [
      { path: "/test-send", label: "发送测试", icon: "send", minRole: "admin" },
      { path: "/captures", label: "抓包调试", icon: "radar" },
      { path: "/audit", label: "审计日志", icon: "scroll-text", minRole: "super_admin" },
      { path: "/admin-users", label: "后台账号", icon: "user-cog", minRole: "super_admin" },
      { path: "/notify", label: "事件通知", icon: "webhook", minRole: "super_admin" },
      { path: "/ui", label: "组件总览", icon: "component", minRole: "admin", devOnly: true },
    ],
  },
];

/** 按角色与环境过滤后的导航：角色低于 minRole 的看不到入口（还不知道角色时按只读处理），正式环境看不到 devOnly 的页面，空的分组整组隐藏。 */
export function visibleNav(role: Role | undefined, dev: boolean): NavGroupDef[] {
  return NAV.map((g) => ({ ...g, items: g.items.filter((it) => (!it.minRole || roleAtLeast(role, it.minRole)) && (!it.devOnly || dev)) })).filter((g) => g.items.length > 0);
}

export function findNav(path: string): { group: NavGroupDef; item: NavEntry } | null {
  for (const group of NAV) for (const item of group.items) if (path === item.path || path.startsWith(item.path + "/")) return { group, item };
  return null;
}
